import type { Socket } from "socket.io-client";
import { computed, type MaybeRefOrGetter, toValue } from "vue";

import { useConfigStore } from "@/stores/config.ts";
import type { ContentBaseItem } from "@/types/content.d.ts";

import useAuthSockets from "./useAuthSocket.ts";

const useFeedSocket = () => {
  const configStore = useConfigStore();

  const socketUrl = configStore.remoteConfig.searchupdate_socket_base;
  const { initializeSocket, isSocketAuthenticated, addSocketListeners } =
    useAuthSockets();
  let feedSocket: Socket | null;
  const isFeedSocketAuthenticated = computed(() => {
    return isSocketAuthenticated.value;
  });

  async function subscribeToSearchUpdated(searchUpdateId: string) {
    if (feedSocket === null) {
      await startUpFeedSocket();
    }
    feedSocket?.emit("subscribe", searchUpdateId);
  }

  async function unsubscribeOfSearch(searchUpdateId: string) {
    if (feedSocket === null) {
      await startUpFeedSocket();
    }

    feedSocket?.emit("unsubscribe", searchUpdateId);
  }

  async function resubscribeToStaleSearch(searchUpdateId: string) {
    if (feedSocket === null) {
      await startUpFeedSocket();
    }

    await subscribeToSearchUpdated(searchUpdateId);
  }

  async function handleSearchUpdatesOnSpecificSearch(
    searchUpdateId: string,
    itemArray: MaybeRefOrGetter<ContentBaseItem[]> | undefined,
    newItemArray: MaybeRefOrGetter<Set<string>> | undefined,
  ) {
    if (feedSocket === null) {
      await startUpFeedSocket();
    }

    const newItems = toValue(newItemArray);
    const existingItems = toValue(itemArray);

    if (!newItems || !existingItems) {
      // eslint-disable-next-line no-console -- Want to throw a warning when there is no valid arrays, as we can't provide fallbacks
      console.warn("Please provide a item and newItem Array");
      return;
    }

    feedSocket?.on("search_update", (event: string) => {
      const { item, search_update_id } = JSON.parse(event) as {
        item: ContentBaseItem;
        search_update_id: string;
      };

      if (searchUpdateId === search_update_id) {
        existingItems.push(item);
        newItems.add(item.urn);
      }
    });
  }

  async function handleSearchUpdates(
    itemArray: MaybeRefOrGetter<ContentBaseItem[]>,
    newItemArray: MaybeRefOrGetter<string[]>,
  ) {
    if (feedSocket === null) {
      await startUpFeedSocket();
    }

    feedSocket?.on("search_update", (event: string) => {
      const { item } = JSON.parse(event) as {
        item: ContentBaseItem;
        search_update_id: string;
      };

      toValue(itemArray).push(item);
      toValue(newItemArray).push(item.urn);
    });
  }

  async function startUpFeedSocket() {
    if (feedSocket && feedSocket.active) return;
    try {
      feedSocket = await initializeSocket(socketUrl);

      addSocketListeners(
        [
          {
            callback: resubscribeToStaleSearch,
            eventName: "search_update_stale",
          },
        ],
        feedSocket,
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("ERROR IN FEED SOCKET STARTUP", e);
      feedSocket = null;
    }
  }

  return {
    startUpFeedSocket,
    isFeedSocketAuthenticated,
    subscribeToSearchUpdated,
    unsubscribeOfSearch,
    handleSearchUpdates,
    handleSearchUpdatesOnSpecificSearch,
  };
};

export default useFeedSocket;
